import React from "react";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";

const unescapeChatText = (value) =>
  String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\\n/g, "\n");

const OPTION_LINE = /^(?:[-*]\s+)?(?:\(([a-zA-Z])\)|([a-zA-Z])\))\s*(.*)$/;

const isBulletLine = (line) => /^-\s+/.test(line) && !OPTION_LINE.test(line.trim());

const parseOptionLine = (line) => {
  const match = String(line || "")
    .trim()
    .match(OPTION_LINE);
  if (!match) return null;
  return {
    letter: String(match[1] || match[2] || "").toLowerCase(),
    text: match[3] || "",
  };
};

const parseInlineOptions = (line) => {
  const source = String(line || "").trim();
  const matches = [...source.matchAll(/\(([a-zA-Z])\)/g)];
  if (matches.length < 2) return null;

  return matches.map((match, index) => {
    const start = match.index + match[0].length;
    const end = index + 1 < matches.length ? matches[index + 1].index : source.length;
    return {
      letter: String(match[1] || "").toLowerCase(),
      text: source.slice(start, end).trim(),
    };
  });
};

const renderInline = (text) => {
  const nodes = [];
  const pattern = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <strong key={`b-${key++}`} style={{ fontWeight: 700 }}>
        {match[1].trim()}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length ? nodes : text;
};

const collectBlocks = (paragraph) => {
  const blocks = [];
  let texts = [];
  let bullets = [];
  let options = [];

  const flushText = () => {
    if (!texts.length) return;
    blocks.push({ type: "text", lines: texts });
    texts = [];
  };

  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push({ type: "list", items: bullets });
    bullets = [];
  };

  const flushOptions = () => {
    if (!options.length) return;
    blocks.push({ type: "options", items: options });
    options = [];
  };

  paragraph.split("\n").forEach((line) => {
    const inlineOptions = parseInlineOptions(line);
    const option = inlineOptions ? null : parseOptionLine(line);
    if (option || inlineOptions) {
      flushText();
      flushBullets();
      options.push(...(option ? [option] : inlineOptions));
      return;
    }
    if (isBulletLine(line)) {
      flushText();
      flushOptions();
      bullets.push(line.replace(/^-\s+/, ""));
      return;
    }
    flushBullets();
    flushOptions();
    texts.push(line);
  });

  flushText();
  flushBullets();
  flushOptions();
  return blocks;
};

function OptionList({ items }) {
  return (
    <MDBox color="inherit" bgColor="transparent" sx={{ width: "100%", my: 0.75 }}>
      {items.map((item, itemIndex) => (
        <MDBox
          key={`${item.letter}-${itemIndex}`}
          display="flex"
          alignItems="center"
          sx={{
            width: "100%",
            gap: 1,
            px: 1.25,
            py: 0.85,
            mb: itemIndex === items.length - 1 ? 0 : 1,
            borderRadius: "1.35rem",
            backgroundColor: "#fff",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
          }}
        >
          <MDBox
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: "2px solid #c5c9d4",
              backgroundColor: "#fff",
              flexShrink: 0,
            }}
          />
          <MDBox
            sx={{
              px: 1,
              py: 0.2,
              borderRadius: "999px",
              backgroundColor: "#eef1f6",
              color: "#344767",
              fontWeight: 700,
              fontSize: "0.78em",
              lineHeight: 1.4,
              flexShrink: 0,
            }}
          >
            ({item.letter})
          </MDBox>
          <MDBox
            color="inherit"
            bgColor="transparent"
            sx={{ minWidth: 0, overflowWrap: "anywhere", wordBreak: "break-word" }}
          >
            {renderInline(item.text)}
          </MDBox>
        </MDBox>
      ))}
    </MDBox>
  );
}

OptionList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      letter: PropTypes.string,
      text: PropTypes.string,
    })
  ).isRequired,
};

function FormattedMessage({ text }) {
  const normalized = unescapeChatText(text).trim();
  if (!normalized) return null;

  const paragraphs = normalized.split(/\n{2,}/);

  return (
    <MDBox
      color="inherit"
      bgColor="transparent"
      sx={{
        "& p": { m: 0 },
        "& p + p": { mt: 1.25 },
        "& ul": {
          m: 0,
          mt: 0.75,
          mb: 0.75,
          pl: "1.35rem !important",
          listStyleType: "disc",
          listStylePosition: "outside",
        },
        "& ul:first-of-type": { mt: 0 },
        "& ul:last-of-type": { mb: 0 },
        "& li": {
          display: "list-item",
          mb: 0.35,
          pl: "0.15rem",
        },
        "& li:last-of-type": { mb: 0 },
      }}
    >
      {paragraphs.map((paragraph, paragraphIndex) => {
        const blocks = collectBlocks(paragraph);

        return (
          <MDBox
            key={paragraphIndex}
            color="inherit"
            bgColor="transparent"
            sx={paragraphIndex > 0 ? { mt: 1.25 } : undefined}
          >
            {blocks.map((block, blockIndex) => {
              if (block.type === "options") {
                return <OptionList key={blockIndex} items={block.items} />;
              }

              if (block.type === "list") {
                return (
                  <MDBox key={blockIndex} component="ul" color="inherit" bgColor="transparent">
                    {block.items.map((item, itemIndex) => (
                      <MDBox key={itemIndex} component="li" color="inherit" bgColor="transparent">
                        {renderInline(item)}
                      </MDBox>
                    ))}
                  </MDBox>
                );
              }

              return (
                <MDBox key={blockIndex} component="p" color="inherit" bgColor="transparent">
                  {block.lines.map((line, lineIndex) => (
                    <React.Fragment key={lineIndex}>
                      {lineIndex > 0 && <br />}
                      {renderInline(line)}
                    </React.Fragment>
                  ))}
                </MDBox>
              );
            })}
          </MDBox>
        );
      })}
    </MDBox>
  );
}

FormattedMessage.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

FormattedMessage.defaultProps = {
  text: "",
};

export default FormattedMessage;
